import React from "react";
import { Link } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import usePageTitle from "@/hooks/usePageTitle";
import {
    ArrowRight, Phone, Handshake, Trophy, Diamond, Check, Target,
    FileText, Briefcase, DollarSign, Calendar, TrendingUp, ShieldCheck,
    Tag, Stethoscope, Wrench, Wrench as Tool, UtensilsCrossed, ShoppingBag, Sun,
    Download,
} from "lucide-react";

const stats = [
    { label: "Funded since 2018", value: "$420M+" },
    { label: "Approval rate", value: "82%" },
    { label: "Avg. time to fund", value: "24 hrs" },
    { label: "ISO partners", value: "1,200+" },
];

const programs = [
    { icon: FileText, title: "C & D Paper", desc: "Underwriting flexibility for the deals other funders walk away from." },
    { icon: Briefcase, title: "2nd – 8th Positions", desc: "Stack-friendly capital with real underwriters who understand position risk." },
    { icon: DollarSign, title: "Up to $3M Deal Sizes", desc: "Built for established merchants with serious volume and history." },
    { icon: Calendar, title: "Up to 35-Week Terms", desc: "Longer terms keep payments manageable and cash flow healthy." },
    { icon: TrendingUp, title: "$50K+ Monthly Revenue", desc: "Standard floor — higher revenues unlock better pricing." },
    { icon: ShieldCheck, title: "Best for High-Dollar Deals", desc: "We sharpen our pencils on $250K+ requests. Bring your serious files." },
];

const isoPerks = [
    { icon: Phone, title: "We Pick Up", desc: "Real people. Real answers. No phone trees, no ticket queues." },
    { icon: Handshake, title: "We Fund", desc: "Fast approvals. Real closings. We close what we approve." },
    { icon: Trophy, title: "We Perform", desc: "Built for high-dollar deals. We sharpen pricing on volume." },
];

const whyIso = [
    { title: "Direct Access to Decision-Makers", desc: "No phone trees. No runaround. Underwriters answer their own line." },
    { title: "Fast Approvals + Real Closings", desc: "We move quickly so you can too. Same-day decisions on complete files." },
    { title: "Built for High-Dollar Deals", desc: "$250K – $3M deal sizes. Built for experienced ISOs with volume." },
    { title: "Aggressive Early Payment Discounts", desc: "Maximize your return. Get paid faster on early renewals." },
];

const industries = [
    { icon: Stethoscope, label: "Healthcare" },
    { icon: Tool, label: "Trade" },
    { icon: Wrench, label: "Service" },
    { icon: UtensilsCrossed, label: "Food & Beverage" },
    { icon: ShoppingBag, label: "Retail" },
    { icon: Sun, label: "Seasonal" },
];

const testimonials = [
    {
        quote: "We funded a $1.4M food-distribution deal in three days flat. Lended's underwriter called me back in twelve minutes. Twelve. That's the difference.",
        name: "Marcus T.",
        role: "Senior ISO Partner",
        firm: "Atlas Capital Group",
    },
    {
        quote: "Fourth position, ugly bank statements, healthy revenue — every other funder passed. Lended sharpened the pencil and closed it. My merchant kept their doors open.",
        name: "Daniela R.",
        role: "Owner",
        firm: "Cardinal Funding",
    },
    {
        quote: "I've placed eight files with Lended this quarter. Zero claw-backs, every commission paid the day after funding. Cleanest paper-trail in the industry.",
        name: "James W.",
        role: "Managing Broker",
        firm: "Highline ISO",
    },
];

export default function Lenders() {
    usePageTitle("For Lenders & ISO Partners", "Direct funder for ISOs and brokers. Fast approvals, real closings, deals up to $3M. We pick up. We fund. We perform.");
    return (
        <>
            {/* HERO */}
            <section className="relative bg-white overflow-hidden" data-testid="lenders-hero-section">
                <div className="absolute inset-0 bg-grid pointer-events-none" />
                <div className="absolute -top-20 -right-32 w-[520px] h-[520px] bg-[#FF6B1A]/10 blur-3xl rounded-full pointer-events-none" />
                <div className="container-lended relative pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="divider-orange" />
                            <span className="eyebrow">For Lenders, ISOs & Brokers</span>
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                            Tired of chasing lenders?{" "}
                            <span className="text-[#FF6B1A]">We&nbsp;fund.</span>
                        </h1>
                        <p className="mt-7 text-lg md:text-xl text-black/70 max-w-2xl leading-relaxed">
                            No chasing. No guessing. Just closings. Built for ISOs moving serious volume — direct
                            funder for deal sizes up to <strong className="text-black">$3,000,000</strong>, decisions in 4 hours,
                            commissions paid the next business day.
                        </p>

                        <div className="mt-9 flex flex-wrap gap-4">
                            <a href="#apply" className="btn-orange" data-testid="lenders-submit-cta">
                                Submit a Deal <ArrowRight size={18} />
                            </a>
                            <Link to="/iso-partners" className="btn-outline-black" data-testid="lenders-iso-cta">
                                ISO Partner Program
                            </Link>
                        </div>

                        {/* Three pillars */}
                        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {isoPerks.map((p) => (
                                <div key={p.title} className="border-l-4 border-[#FF6B1A] pl-5" data-testid={`lenders-pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                    <div className="flex items-center gap-2 text-[#FF6B1A]">
                                        <p.icon size={18} />
                                        <span className="font-display font-bold text-lg uppercase tracking-tight">{p.title}.</span>
                                    </div>
                                    <p className="text-sm text-black/65 mt-1.5">{p.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5" id="apply">
                        <LeadForm defaultAudience="iso" />
                    </div>
                </div>
            </section>

            {/* STATS BAR */}
            <section className="bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="lenders-stats-section">
                <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
                <div className="container-lended relative py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
                    {stats.map((s) => (
                        <div key={s.label}>
                            <div className="stat-num text-4xl md:text-6xl text-[#FF6B1A]">{s.value}</div>
                            <div className="text-xs md:text-sm uppercase tracking-[0.18em] mt-3 text-white/60">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* WHY ISOS CHOOSE US + ACTIVE PROGRAMS */}
            <section className="bg-white py-20 md:py-28" data-testid="lenders-why-programs-section">
                <div className="container-lended grid lg:grid-cols-2 gap-16">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <Diamond className="text-[#FF6B1A]" size={22} />
                            <span className="eyebrow">Why ISOs Choose Us</span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[0.98] max-w-md">
                            Built for the deals other funders walk away from.
                        </h2>
                        <ul className="mt-10 space-y-7">
                            {whyIso.map((w) => (
                                <li key={w.title} className="flex gap-4 border-b border-black/10 pb-7 last:border-b-0">
                                    <div className="shrink-0 w-9 h-9 border-2 border-[#FF6B1A] flex items-center justify-center">
                                        <Check className="text-[#FF6B1A]" size={18} />
                                    </div>
                                    <div>
                                        <div className="font-display text-lg font-bold">{w.title}</div>
                                        <p className="text-black/65 mt-1">{w.desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <TrendingUp className="text-[#FF6B1A]" size={22} />
                            <span className="eyebrow">Active Programs</span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[0.98]">
                            More points. <br /> More rewards. <br /> More growth.
                        </h2>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" id="programs">
                            {programs.map((p) => (
                                <div key={p.title} className="bg-[#F8F5F2] p-6 hover:bg-white hover:shadow-[6px_6px_0_0_#FF6B1A] hover:-translate-y-0.5 transition-all border border-black/5" data-testid={`lenders-program-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                    <p.icon className="text-[#FF6B1A]" size={28} />
                                    <div className="font-display font-bold text-lg mt-4">{p.title}</div>
                                    <p className="text-sm text-black/60 mt-1.5 leading-relaxed">{p.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* GUIDELINES PDF DOWNLOAD */}
            <section className="bg-white py-16 md:py-20 border-t border-black/10" data-testid="lenders-guidelines-section">
                <div className="container-lended">
                    <div className="grid lg:grid-cols-12 gap-10 items-center bg-[#0A0A0A] text-white p-8 md:p-12 relative overflow-hidden">
                        <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                        <div className="absolute -right-16 -top-16 w-[400px] h-[400px] bg-[#FF6B1A]/15 blur-3xl rounded-full pointer-events-none" />
                        <div className="lg:col-span-7 relative">
                            <div className="flex items-center gap-3 mb-3">
                                <FileText className="text-[#FF6B1A]" size={22} />
                                <span className="eyebrow text-[#FF6B1A]">Submission Guidelines</span>
                            </div>
                            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[0.98]">
                                The full ISO playbook. <br />
                                <span className="text-[#FF6B1A]">One-page PDF.</span>
                            </h2>
                            <p className="text-white/70 mt-5 max-w-xl">
                                14-pt payouts, position appetite, deal sizes, term lengths, revenue floors, and exactly
                                what we close. Print it, pin it, send it to your team.
                            </p>
                        </div>
                        <div className="lg:col-span-5 relative flex flex-col gap-3">
                            <a
                                href="/downloads/Lended-ISO-Guidelines.pdf"
                                download
                                className="btn-orange justify-center text-base"
                                data-testid="lenders-download-pdf"
                            >
                                <Download size={18} /> Download PDF Guidelines
                            </a>
                            <a
                                href="/downloads/Lended-ISO-Guidelines.pdf"
                                target="_blank"
                                rel="noreferrer"
                                className="text-center text-sm text-white/60 hover:text-white"
                                data-testid="lenders-view-pdf"
                            >
                                or view in browser
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* AGGRESSIVE EARLY PAYMENT DISCOUNTS */}
            <section className="bg-[#F8F5F2] py-20" data-testid="lenders-discount-section">
                <div className="container-lended grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <img
                            src="https://images.pexels.com/photos/4175028/pexels-photo-4175028.jpeg"
                            alt="ISO partnership"
                            className="w-full h-[420px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                        />
                    </div>
                    <div className="lg:col-span-7">
                        <Tag className="text-[#FF6B1A]" size={32} />
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-4 leading-[0.98]">
                            Aggressive early <br /> payment discounts.
                        </h2>
                        <p className="text-black/70 text-lg mt-6 max-w-xl">
                            Maximize your return. Merchants who pay early get steep discounts. ISOs get faster renewals.
                            Everyone wins. Ask your underwriter about our APD schedule on your next file.
                        </p>
                        <div className="mt-8 flex gap-4">
                            <Link to="/iso-partners" className="btn-black" data-testid="lenders-discount-iso-cta">Become an ISO Partner</Link>
                            <a href="#apply" className="btn-outline-black">Submit a Deal</a>
                        </div>
                    </div>
                </div>
            </section>

            {/* INDUSTRIES SERVED */}
            <section className="bg-white py-20 md:py-28" data-testid="lenders-industries-section">
                <div className="container-lended">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="eyebrow">Industries We Fund</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            Real capital for the businesses that built this country.
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10">
                        {industries.map((i) => (
                            <div key={i.label} className="bg-white aspect-square flex flex-col items-center justify-center gap-3 hover:bg-[#0A0A0A] hover:text-white group transition-colors" data-testid={`lenders-industry-${i.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                <i.icon className="text-[#FF6B1A] group-hover:text-[#FF6B1A]" size={32} />
                                <div className="font-display font-bold uppercase tracking-tight text-sm">{i.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-[#0A0A0A] text-white py-20 md:py-28 relative overflow-hidden" data-testid="lenders-testimonials-section">
                <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                <div className="container-lended relative">
                    <div className="max-w-3xl mb-16">
                        <span className="eyebrow text-[#FF6B1A]">Word From the Field</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            ISOs and merchants don't sugar-coat. <span className="text-[#FF6B1A]">We don't either.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-white/10">
                        {testimonials.map((t, i) => (
                            <div key={i} className="bg-[#0A0A0A] p-8 md:p-10 flex flex-col" data-testid={`lenders-testimonial-${i}`}>
                                <div className="font-display text-7xl text-[#FF6B1A] leading-none mb-4">"</div>
                                <p className="text-white/85 leading-relaxed flex-1">{t.quote}</p>
                                <div className="mt-8 pt-6 border-t border-white/15">
                                    <div className="font-display font-bold">{t.name}</div>
                                    <div className="text-sm text-white/50">{t.role} · {t.firm}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-white py-20 md:py-28" data-testid="lenders-final-cta-section">
                <div className="container-lended">
                    <div className="bg-[#F8F5F2] border-2 border-[#0A0A0A] p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <Target className="text-[#FF6B1A]" size={36} />
                            <h2 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight mt-3 leading-[0.95]">
                                Have a deal? <br /> <span className="text-[#FF6B1A]">Let's get it funded.</span>
                            </h2>
                            <p className="text-black/70 text-lg mt-4">Send your next deal. We'll prove it.</p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3">
                            <a href="#apply" className="btn-orange text-base justify-center" data-testid="lenders-final-apply-cta">
                                Submit a Deal <ArrowRight size={18} />
                            </a>
                            <Link to="/contact" className="btn-outline-black text-base justify-center">
                                Contact Us
                            </Link>
                            <a href="mailto:submissions@lended.com" className="text-sm text-black/65 hover:text-[#FF6B1A] mt-1 text-center">
                                or email submissions@lended.com
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
